/**
 * The report workspace — the core screen (Phase 3, task 4). Self-contained: the reviewer
 * reads the report PDF AND the letter of instruction inline, and sees the compliance
 * checklist, risk matrix, and errors list, without downloading anything. Presence sets
 * status='reviewing' while open (see reviewer/usePresence.ts); the only input expected of
 * the reviewer is a note and marking the report done.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type {
  AnnotationRow,
  BankRow,
  CheckResultRow,
  CheckSchemaRow,
  ErrorFindingRow,
  ReportRow,
  RiskResultRow,
  RiskSchemaRow,
  ValuerRow,
} from '../../reviewer/types';
import { useDocumentUrls } from '../../reviewer/useDocumentUrls';
import { useReviewerPresence } from '../../reviewer/usePresence';
import { useReportAnnotations } from '../../reviewer/useAnnotations';
import { fetchRevisionChain, fetchReportCost, type ReportCostSummary } from '../../reviewer/queries';
import { replacePageInUrl } from '../../reviewer/router';
import { checkTargetDoc } from '../../reviewer/types';
import { fetchEta } from '../../api';
import { reviewerEtaSentence, type EtaInfo } from '../../lib/eta';
import { useNowTick } from '../../lib/waiting';
import {
  setReportStatus,
  setReviewerNote,
  rerunCompliance,
  createAnnotation,
  updateAnnotation,
  deleteAnnotation,
  sendFeedbackToValuer,
  ApiError,
} from '../../api';
import { Banner, Button, Tile } from '../../components/ui';
import { StatusChip } from '../../components/reviewer/StatusChip';
import { PdfViewer, type PdfViewerHandle } from '../../components/PdfViewer';
import { ReportSummarySection } from '../../components/reviewer/ReportSummarySection';
import { ValuerNotesSection } from '../../components/reviewer/ValuerNotesSection';
import { ChecklistSection } from '../../components/reviewer/ChecklistSection';
import { RiskSection } from '../../components/reviewer/RiskSection';
import { ErrorsSection } from '../../components/reviewer/ErrorsSection';
import { AddToCalendar } from '../../components/reviewer/AddToCalendar';
import { MarkupToolbar, type MarkupTool } from '../../components/reviewer/MarkupToolbar';
import { MarkupLayer } from '../../components/reviewer/MarkupLayer';
import { ComplianceHighlightLayer, type HighlightFinding } from '../../components/reviewer/ComplianceHighlightLayer';
import { NotesPanel } from '../../components/reviewer/NotesPanel';
import { RevisionSelector } from '../../components/reviewer/RevisionSelector';

interface ReportWorkspaceProps {
  report: ReportRow | null;
  valuers: ValuerRow[];
  banks: BankRow[];
  checkSchema: CheckSchemaRow[];
  riskSchema: RiskSchemaRow[];
  checkResults: CheckResultRow[];
  riskResults: RiskResultRow[];
  errorFindings: ErrorFindingRow[];
  initialDoc?: 'report' | 'loi';
  initialPage?: number;
  /** Phase 6: shows the "Re-run compliance" action for accounts with admin access. */
  isAdmin?: boolean;
  onBack: () => void;
  /** Phase 7: open a different round of this same report thread. */
  onOpenRevision: (reportId: string) => void;
}

const RESULTS_READY = new Set(['checked', 'reviewing', 'done', 'changes_requested']);
const IN_PIPELINE = new Set(['uploaded', 'queued', 'checking']);

/** Part VIII: the compliance-highlight toggle is a simple per-reviewer preference, persisted locally. Default ON. */
const HIGHLIGHTS_PREF_KEY = 'qa:showComplianceHighlights';

function loadHighlightsPref(): boolean {
  try {
    return window.localStorage.getItem(HIGHLIGHTS_PREF_KEY) !== 'off';
  } catch {
    return true;
  }
}

function saveHighlightsPref(on: boolean): void {
  try {
    window.localStorage.setItem(HIGHLIGHTS_PREF_KEY, on ? 'on' : 'off');
  } catch {
    // Preference persistence is best-effort; the toggle still works for the session.
  }
}

/** Numbering matches the flatten order used when feedback is sent (page, then created order) - see worker/src/markup/flatten.ts. */
function numberAnnotations(annotations: AnnotationRow[]): Map<string, number> {
  const ordered = annotations
    .slice()
    .sort((a, b) => a.page - b.page || new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
  return new Map(ordered.map((a, i) => [a.id, i + 1]));
}

export function ReportWorkspace({
  report,
  valuers,
  banks,
  checkSchema,
  riskSchema,
  checkResults,
  riskResults,
  errorFindings,
  initialDoc,
  initialPage,
  isAdmin,
  onBack,
  onOpenRevision,
}: ReportWorkspaceProps) {
  const [activeDoc, setActiveDoc] = useState<'report' | 'loi'>(initialDoc ?? 'report');
  const reportViewerRef = useRef<PdfViewerHandle | null>(null);
  const loiViewerRef = useRef<PdfViewerHandle | null>(null);
  const jumpedInitial = useRef(false);

  const [note, setNote] = useState(report?.reviewerNotes ?? '');
  const [noteSaved, setNoteSaved] = useState(true);
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [rerunning, setRerunning] = useState(false);
  const [rerunMessage, setRerunMessage] = useState<string | null>(null);

  const [activeTool, setActiveTool] = useState<MarkupTool>('none');
  /** Ids of annotations created this session, in order - "Undo" removes the last one. */
  const [undoStack, setUndoStack] = useState<string[]>([]);
  const [focusNoteId, setFocusNoteId] = useState<string | null>(null);
  const [markupError, setMarkupError] = useState<string | null>(null);
  const [sendingFeedback, setSendingFeedback] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState<string | null>(null);
  const [revisionChain, setRevisionChain] = useState<ReportRow[]>([]);
  const [showHighlights, setShowHighlights] = useState(loadHighlightsPref);
  const [cost, setCost] = useState<ReportCostSummary | null>(null);
  const [eta, setEta] = useState<EtaInfo | null>(null);

  const reportId = report?.id ?? null;
  const docUrls = useDocumentUrls(reportId ?? '');
  const { annotations, reload: reloadAnnotations } = useReportAnnotations(reportId);
  useReviewerPresence(reportId ?? '', Boolean(reportId) && RESULTS_READY.has(report?.status ?? ''));

  const numberById = useMemo(() => numberAnnotations(annotations), [annotations]);

  const inPipeline = IN_PIPELINE.has(report?.status ?? '');
  const now = useNowTick(inPipeline ? 15_000 : 3_600_000);

  // Part V: per-report AI spend, visible in the workspace (reviewer + admin can read the ledger).
  useEffect(() => {
    if (!reportId) return;
    let cancelled = false;
    fetchReportCost(reportId)
      .then((summary) => {
        if (!cancelled) setCost(summary);
      })
      .catch(() => {
        if (!cancelled) setCost(null);
      });
    return () => {
      cancelled = true;
    };
  }, [reportId, report?.status]);

  // Part IX: live ETA while the report is still in the pipeline.
  useEffect(() => {
    if (!inPipeline) return;
    let cancelled = false;
    fetchEta()
      .then((info) => {
        if (!cancelled) setEta(info);
      })
      .catch(() => {
        if (!cancelled) setEta(null);
      });
    return () => {
      cancelled = true;
    };
  }, [inPipeline, reportId]);

  // Part VIII: every failed/flagged finding with a page becomes a highlight,
  // split by which document its page_ref points into.
  const highlightFindings = useMemo(() => {
    const all: Array<HighlightFinding & { doc: 'report' | 'loi' }> = [];
    const categoryByKey = new Map(checkSchema.map((c) => [c.checkKey, c.category] as const));
    const labelByCheckKey = new Map(checkSchema.map((c) => [c.checkKey, c.label] as const));
    const labelByRiskKey = new Map(riskSchema.map((r) => [r.riskKey, r.label] as const));

    for (const c of checkResults) {
      if (c.pageRef === null || (c.status !== 'fail' && c.status !== 'flag')) continue;
      all.push({
        id: `check:${c.id}`,
        doc: checkTargetDoc(categoryByKey.get(c.checkKey) ?? ''),
        page: c.pageRef,
        tone: c.status,
        label: labelByCheckKey.get(c.checkKey) ?? c.checkKey,
        snippet: c.evidenceSnippet,
      });
    }
    for (const r of riskResults) {
      if (r.pageRef === null || r.status === 'ok') continue;
      all.push({
        id: `risk:${r.id}`,
        doc: 'report',
        page: r.pageRef,
        tone: r.status === 'comment_inadequate' ? 'flag' : 'fail',
        label: `${labelByRiskKey.get(r.riskKey) ?? r.riskKey} (${r.status.replace(/_/g, ' ')})`,
        snippet: r.commentText,
      });
    }
    for (const e of errorFindings) {
      if (e.pageRef === null) continue;
      all.push({
        id: `error:${e.id}`,
        doc: 'report',
        page: e.pageRef,
        tone: e.severity === 'high' ? 'fail' : 'flag',
        label: e.description ?? e.errorType,
        snippet: e.evidenceSnippet,
      });
    }
    return all;
  }, [checkSchema, riskSchema, checkResults, riskResults, errorFindings]);

  const reportHighlights = useMemo(() => highlightFindings.filter((f) => f.doc === 'report'), [highlightFindings]);
  const loiHighlights = useMemo(() => highlightFindings.filter((f) => f.doc === 'loi'), [highlightFindings]);

  function toggleHighlights() {
    setShowHighlights((on) => {
      saveHighlightsPref(!on);
      return !on;
    });
  }

  useEffect(() => {
    setNote(report?.reviewerNotes ?? '');
    setNoteSaved(true);
  }, [report?.id, report?.reviewerNotes]);

  useEffect(() => {
    setActiveTool('none');
    setUndoStack([]);
    setFeedbackMessage(null);
    setMarkupError(null);
  }, [report?.id]);

  useEffect(() => {
    if (jumpedInitial.current || !initialPage || docUrls.status !== 'ready') return;
    jumpedInitial.current = true;
    const ref = (initialDoc ?? 'report') === 'loi' ? loiViewerRef : reportViewerRef;
    ref.current?.goToPage(initialPage);
  }, [docUrls.status, initialDoc, initialPage]);

  useEffect(() => {
    if (!reportId) {
      setRevisionChain([]);
      return;
    }
    let cancelled = false;
    fetchRevisionChain(reportId)
      .then((chain) => {
        if (!cancelled) setRevisionChain(chain);
      })
      .catch(() => {
        if (!cancelled) setRevisionChain([]);
      });
    return () => {
      cancelled = true;
    };
  }, [reportId]);

  if (!report || !reportId) {
    return (
      <main className="main main--wide">
        <Button variant="ghost" onClick={onBack}>
          ← Back
        </Button>
        <Tile className="tile--pad-lg">
          <p className="muted">That report couldn’t be found.</p>
        </Tile>
      </main>
    );
  }

  const bank = banks.find((b) => b.id === report.ordererBankId);
  const standingInstructionLabel = bank ? bank.name : 'Institute baseline';
  const resultsReady = RESULTS_READY.has(report.status);
  const valuerName = valuers.find((v) => v.id === report.valuerId)?.displayName ?? 'Valuer';

  function jumpTo(doc: 'report' | 'loi', page: number) {
    setActiveDoc(doc);
    const ref = doc === 'loi' ? loiViewerRef : reportViewerRef;
    ref.current?.goToPage(page);
    replacePageInUrl(reportId!, doc, page);
  }

  async function saveNote() {
    setSaving(true);
    setActionError(null);
    try {
      await setReviewerNote(reportId!, note);
      setNoteSaved(true);
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Could not save the note.');
    } finally {
      setSaving(false);
    }
  }

  async function handleCreateAnnotation(input: { page: number; type: 'highlight' | 'note' | 'shape'; coordinates: unknown }) {
    setMarkupError(null);
    try {
      const created = await createAnnotation(reportId!, input);
      setUndoStack((stack) => [...stack, created.id]);
      if (input.type === 'note') setFocusNoteId(created.id);
      await reloadAnnotations();
      setActiveTool('none');
    } catch (err) {
      setMarkupError(err instanceof ApiError ? err.message : 'Could not save that markup.');
    }
  }

  async function handleUndo() {
    const lastId = undoStack[undoStack.length - 1];
    if (!lastId) return;
    setUndoStack((stack) => stack.slice(0, -1));
    await handleDeleteAnnotation(lastId);
  }

  async function handleUpdateAnnotationText(annotationId: string, text: string) {
    setMarkupError(null);
    try {
      await updateAnnotation(reportId!, annotationId, { text });
      await reloadAnnotations();
    } catch (err) {
      setMarkupError(err instanceof ApiError ? err.message : 'Could not save that note.');
    }
  }

  async function handleDeleteAnnotation(annotationId: string) {
    setMarkupError(null);
    try {
      await deleteAnnotation(reportId!, annotationId);
      setUndoStack((stack) => stack.filter((id) => id !== annotationId));
      await reloadAnnotations();
    } catch (err) {
      setMarkupError(err instanceof ApiError ? err.message : 'Could not remove that markup.');
    }
  }

  async function handleSendFeedback() {
    setSendingFeedback(true);
    setMarkupError(null);
    setFeedbackMessage(null);
    try {
      const result = await sendFeedbackToValuer(reportId!);
      setFeedbackMessage(
        `Feedback sent — ${result.itemCount} item${result.itemCount === 1 ? '' : 's'}${
          result.attached ? '' : ' (the file was too large to attach; the valuer can view it in the feedback screen)'
        }.`,
      );
    } catch (err) {
      setMarkupError(err instanceof ApiError ? err.message : 'Could not send feedback to the valuer.');
    } finally {
      setSendingFeedback(false);
    }
  }

  async function rerun() {
    setRerunning(true);
    setActionError(null);
    setRerunMessage(null);
    try {
      await rerunCompliance(reportId!);
      setRerunMessage('Queued - this report will be re-checked shortly.');
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Could not re-run compliance for this report.');
    } finally {
      setRerunning(false);
    }
  }

  async function markDone() {
    setSaving(true);
    setActionError(null);
    try {
      if (note.trim() && !noteSaved) await setReviewerNote(reportId!, note);
      await setReportStatus(reportId!, 'done');
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Could not mark this report done.');
    } finally {
      setSaving(false);
    }
  }

  // Shared between the normal below-viewer location and the full-screen side panel
  // (passed to PdfViewer's sidePanel prop) - one definition, so both stay in sync.
  const markupNotesPanel = (
    <>
      <div className="section__head">
        <h2 className="section__title">Markup notes</h2>
      </div>
      <Tile className="stack">
        {markupError ? <Banner tone="error">{markupError}</Banner> : null}
        {feedbackMessage ? <Banner tone="good">{feedbackMessage}</Banner> : null}
        <NotesPanel
          annotations={annotations}
          numberById={numberById}
          focusId={focusNoteId}
          onJump={(page) => jumpTo('report', page)}
          onUpdateText={handleUpdateAnnotationText}
          onDelete={handleDeleteAnnotation}
        />
        <Button onClick={handleSendFeedback} disabled={sendingFeedback || annotations.length === 0 || !resultsReady} block>
          {sendingFeedback ? 'Sending…' : 'Send feedback to valuer'}
        </Button>
      </Tile>
    </>
  );

  return (
    <main className="main main--wide">
      <div className="page-head">
        <div>
          <Button variant="ghost" onClick={onBack}>
            ← Back
          </Button>
          <h1 className="page-title">{report.addressRaw || 'Address not recorded'}</h1>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
          {isAdmin ? (
            <Button variant="secondary" onClick={() => void rerun()} disabled={rerunning}>
              {rerunning ? 'Queuing…' : 'Re-run compliance'}
            </Button>
          ) : null}
          <AddToCalendar reportId={report.id} addressRaw={report.addressRaw} valuerName={valuerName} />
          <StatusChip status={report.status} />
        </div>
      </div>
      {rerunMessage ? <Banner tone="good">{rerunMessage}</Banner> : null}

      <RevisionSelector chain={revisionChain} currentReportId={reportId!} onSelect={onOpenRevision} />

      <div className="workspace">
        <div className="workspace__docs">
          <div className="doc-tabs">
            <button
              type="button"
              className={`doc-tab${activeDoc === 'report' ? ' doc-tab--active' : ''}`}
              onClick={() => setActiveDoc('report')}
            >
              Valuation report
            </button>
            <button
              type="button"
              className={`doc-tab${activeDoc === 'loi' ? ' doc-tab--active' : ''}`}
              onClick={() => setActiveDoc('loi')}
            >
              Letter of instruction
            </button>
            <label className="highlight-toggle">
              <input type="checkbox" checked={showHighlights} onChange={toggleHighlights} />
              <span>Show compliance highlights</span>
            </label>
          </div>

          {docUrls.status === 'error' ? <Banner tone="error">{docUrls.message}</Banner> : null}

          <div style={{ display: activeDoc === 'report' ? 'block' : 'none' }}>
            <PdfViewer
              ref={reportViewerRef}
              url={docUrls.status === 'ready' ? docUrls.urls.report : null}
              onPageChange={(p) => replacePageInUrl(reportId!, 'report', p)}
              markupToolbar={
                <MarkupToolbar
                  activeTool={activeTool}
                  onSelectTool={setActiveTool}
                  onUndo={() => void handleUndo()}
                  canUndo={undoStack.length > 0}
                />
              }
              sidePanel={markupNotesPanel}
              renderOverlay={(page, doc) => (
                <>
                  {showHighlights ? <ComplianceHighlightLayer page={page} doc={doc} findings={reportHighlights} /> : null}
                  <MarkupLayer
                    page={page}
                    annotations={annotations}
                    numberById={numberById}
                    activeTool={activeTool}
                    onCreate={handleCreateAnnotation}
                    onMarkerClick={setFocusNoteId}
                  />
                </>
              )}
            />
          </div>
          <div style={{ display: activeDoc === 'loi' ? 'block' : 'none' }}>
            <PdfViewer
              ref={loiViewerRef}
              url={docUrls.status === 'ready' ? docUrls.urls.letterOfInstruction : null}
              onPageChange={(p) => replacePageInUrl(reportId!, 'loi', p)}
              renderOverlay={(page, doc) =>
                showHighlights ? <ComplianceHighlightLayer page={page} doc={doc} findings={loiHighlights} /> : null
              }
            />
          </div>

          {activeDoc === 'report' ? <section className="markup-panel">{markupNotesPanel}</section> : null}
        </div>

        <div className="workspace__results stack">
          <ValuerNotesSection notes={report.valuerNotes} />

          {!resultsReady ? (
            <Tile>
              <p className="muted">This report is still being checked — results will appear here as soon as they’re ready.</p>
              {inPipeline && eta ? <p className="muted">{reviewerEtaSentence(eta, report.submittedAt, now)}</p> : null}
            </Tile>
          ) : null}

          <ReportSummarySection summary={report.reportSummary} checked={resultsReady} />

          <ChecklistSection
            schema={checkSchema}
            results={checkResults}
            standingInstructionLabel={standingInstructionLabel}
            checked={resultsReady}
            onJump={jumpTo}
          />
          <RiskSection schema={riskSchema} results={riskResults} checked={resultsReady} onJump={jumpTo} />
          <ErrorsSection errors={errorFindings} onJump={jumpTo} checked={resultsReady} />

          {cost && cost.callCount > 0 ? (
            <p className="muted" style={{ fontSize: 'var(--text-small, 0.8125rem)' }}>
              AI processing cost: ${cost.totalUsd.toFixed(4)} USD across {cost.callCount} call
              {cost.callCount === 1 ? '' : 's'} (hard cap $1.50 per report).
            </p>
          ) : null}

          <section className="section">
            <div className="section__head">
              <h2 className="section__title">Reviewer note</h2>
            </div>
            <Tile className="stack">
              {actionError ? <Banner tone="error">{actionError}</Banner> : null}
              <textarea
                className="input note-textarea"
                placeholder="Add a note for this report (optional)…"
                value={note}
                onChange={(e) => {
                  setNote(e.target.value);
                  setNoteSaved(false);
                }}
                rows={4}
              />
              <div className="workspace__actions">
                <Button variant="secondary" onClick={saveNote} disabled={saving || noteSaved}>
                  {noteSaved ? 'Saved' : 'Save note'}
                </Button>
                <Button onClick={markDone} disabled={saving || report.status === 'done'}>
                  {report.status === 'done' ? 'Marked done ✓' : 'Mark as done'}
                </Button>
              </div>
            </Tile>
          </section>
        </div>
      </div>
    </main>
  );
}
